import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { ActivityEvent, ActivityKind } from '@/types'
import type { ActivityFilter } from '@/constants/dashboard'

import Link from 'next/link'
import { Inbox } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDayHeading } from '@/lib/format'
import { countActivityByKind, listActivity } from '@/lib/dashboard/queries'
import { parsePage, pickOption } from '@/lib/dashboard/params'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageHeader } from '@/components/dashboard/page-header'
import { SegmentedLinks } from '@/components/dashboard/segmented-links'
import { ActivityItem } from '@/components/dashboard/activity-item'
import { Pagination } from '@/components/dashboard/pagination'
import { EmptyState, ErrorState } from '@/components/dashboard/states'
import { buttonVariants } from '@/components/ui/button'
import { useQueryHref } from '@/hooks/use-query-href'
import { useRouteLoading } from '@/hooks/use-route-loading'
import { ACTIVITY_FILTERS, PAGE_SIZE } from '@/constants/dashboard'

interface ActivityPageProps {
    now: string
    filter: ActivityFilter
    page: number
    events: ActivityEvent[] | null
    counts: Record<ActivityFilter, number> | null
}

const kindsFor = (filter: ActivityFilter): ActivityKind[] | null => {
    return ACTIVITY_FILTERS.find(option => option.key === filter)?.kinds ?? null
}

export const getServerSideProps: GetServerSideProps<ActivityPageProps> = async ({ query }) => {
    const filter = pickOption(query.type, ACTIVITY_FILTERS, 'all')
    const page = parsePage(query.page)
    const now = new Date()

    try {
        const [events, byKind] = await Promise.all([
            listActivity({ now, kinds: kindsFor(filter), limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }),
            countActivityByKind(now)
        ])

        const counts = Object.fromEntries(ACTIVITY_FILTERS.map(option => {
            const kinds = option.kinds ?? (Object.keys(byKind) as ActivityKind[])

            return [option.key, kinds.reduce((sum, kind) => sum + (byKind[kind] ?? 0), 0)]
        })) as Record<ActivityFilter, number>

        return { props: { now: now.toISOString(), filter, page, events, counts } }
    }
    catch (error) {
        console.error('Could not load activity', error)

        return { props: { now: now.toISOString(), filter, page, events: null, counts: null } }
    }
}

interface DayGroup {
    key: string
    heading: string
    events: ActivityEvent[]
}

const groupByDay = (events: ActivityEvent[], now: string): DayGroup[] => {
    const groups: DayGroup[] = []

    for (const event of events) {
        const key = event.occurredAt.slice(0, 10)
        const last = groups[groups.length - 1]

        if (last && last.key === key) last.events.push(event)
        else groups.push({ key, heading: formatDayHeading(event.occurredAt, now), events: [event] })
    }

    return groups
}

const Activity: FC<ActivityPageProps> = ({ now, filter, page, events, counts }): ReactNode => {
    const loading = useRouteLoading()
    const hrefFor = useQueryHref()

    const total = counts ? counts[filter] : 0
    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
    const groups = events ? groupByDay(events, now) : []

    return (
        <Layout>
            <Seo title='Activity' />

            <PageHeader
                title='Activity'
                description='A running log of sign-ups, plan changes, cancellations and payment issues. Times are in UTC.'
            />

            {!events || !counts ? (
                <ErrorState title="We couldn't load your activity" />
            ) : (
                <section
                    id='activity-log'
                    aria-label='Activity log'
                    className='rounded-xl border border-border bg-card shadow-[0_1px_2px_rgba(16,24,40,0.04)]'
                >
                    <div className='border-b border-border p-4'>
                        <SegmentedLinks
                            label='Filter activity'
                            active={filter}
                            options={ACTIVITY_FILTERS.map(option => ({
                                key: option.key,
                                label: option.label,
                                count: counts[option.key]
                            }))}
                            hrefFor={key => hrefFor({ type: key === 'all' ? null : key, page: null })}
                        />
                    </div>

                    <div aria-busy={loading} className={cn('transition-opacity duration-200', loading && 'opacity-60')}>
                        {groups.length === 0 ? (
                            total > 0 ? (
                                <EmptyState
                                    icon={Inbox}
                                    title='Nothing on this page'
                                    description='There is no more activity this far back.'
                                    action={
                                        <Link href={hrefFor({ page: null })} className={cn(buttonVariants({ variant: 'outline' }), 'h-9 px-3')}>
                                            Back to the latest
                                        </Link>
                                    }
                                />
                            ) : (
                                <EmptyState
                                    icon={Inbox}
                                    title='No activity here yet'
                                    description='New customers, plan changes, cancellations and payment issues will show up here as they happen.'
                                />
                            )
                        ) : (
                            <div className='px-5 py-2'>
                                {groups.map(group => (
                                    <section key={group.key} aria-labelledby={`day-${group.key}`} className='py-3'>
                                        <h3
                                            id={`day-${group.key}`}
                                            className='text-xs font-semibold tracking-wide text-muted-foreground uppercase'
                                        >
                                            {group.heading}
                                        </h3>

                                        <ul className='divide-y divide-border'>
                                            {group.events.map(event => (
                                                <ActivityItem key={event.id} event={event} now={now} timeStyle='clock' />
                                            ))}
                                        </ul>
                                    </section>
                                ))}
                            </div>
                        )}
                    </div>

                    {total > 0 && (
                        <Pagination
                            page={page}
                            pageCount={pageCount}
                            total={total}
                            noun={total === 1 ? 'event' : 'events'}
                        />
                    )}
                </section>
            )}
        </Layout>
    )
}

export default Activity
